import React, { useState, useEffect } from 'react';
import {
  Card as MuiCard,
  CardHeader,
  CardContent,
  Avatar,
  IconButton,
  Collapse,
  Typography,
  Box,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Star,
  StarBorder,
  Mail,
  Phone,
  Business,
  LocationOn,
  Person,
  AttachMoney,
  MoreVert,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from 'src/store';
import { updateContact, selectContactById, loadContactToSlice, fetchQueuedContacts } from 'src/features/account/contacts.slice';
import CustomMarkdown from '../CustomMarkdown';
import { useContactDrawer } from './ContactDrawer';
import ContactEmailButton from './ContactEmailButton';

interface ContactCardV2Props {
  contactId: string;
  expanded: boolean;
  selected: boolean;
  onSelect: () => void;
}

const ContactCardV2 = React.memo(function ContactCardV2({ contactId, expanded, selected, onSelect }: ContactCardV2Props) {
  const contact = useAppSelector(state => selectContactById(state, contactId));
  const [isExpanded, setIsExpanded] = useState<boolean>(expanded);
  const [isFavorite, setIsFavorite] = useState<boolean>(contact?.tags?.includes('isfavorite'));
  const dispatch = useAppDispatch();
  const { openDrawer } = useContactDrawer();

  useEffect(() => {
    if (contact) {
      setIsFavorite(contact?.tags?.includes('isfavorite'));
    } else if (contactId) {
      // If contact is not in the slice, queue it for fetching
      dispatch(loadContactToSlice(contactId));
      // Trigger the debounced fetch
      dispatch(fetchQueuedContacts());
    }
  }, [contact, contactId, dispatch]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleFavoriteToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent the click from bubbling up to parent elements (e.g., Tooltip, CardHeader)
    // which would otherwise close the tooltip or toggle the card expansion.
    event.stopPropagation();
    event.preventDefault();

    const updatedTags = contact.tags?.includes('isfavorite')
      ? contact.tags.filter(tag => tag !== 'isfavorite')
      : [...(contact.tags || []), 'isfavorite'];

    dispatch(updateContact({ contactId, updates: { tags: updatedTags } }));
  };

  const getFirstLine = () => {
    if (contact.first_name || contact.last_name) {
      return `${contact.first_name || ''} ${contact.last_name || ''}`;
    }
    if (contact.email) {
      return contact.email;
    }
    if (contact.phone_number) {
      return contact.phone_number;
    }
    return 'No contact information available';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    }).format(date);
  };

  const renderContactDetails = () => {
    const addressParts = [
      contact.street_address_1,
      contact.street_address_2,
      contact.locality, // Use locality instead of city
      contact.state,
      contact.country_code,
    ].filter(Boolean).join(', '); // Filter out falsy values and join with a comma

    // Build company label more carefully to avoid displaying "null" in case company_size is missing
    const companyLabel = (() => {
      if (contact.company_name && contact.company_size) {
        return `${contact.company_name} - ${contact.company_size} employees`;
      }
      if (contact.company_name) {
        return contact.company_name;
      }
      if (contact.company_size) {
        return `${contact.company_size} employees`;
      }
      return null;
    })();

    const details = [
      { icon: <Business />, label: companyLabel, key: 'company_name' },
      { icon: <LocationOn />, label: addressParts || null, key: 'location' }, // Use addressParts
      { icon: <AttachMoney />, label: contact.deal_size ? `Deal size: ${contact.deal_size}` : null, key: 'deal_size' },
      { icon: <Person />, label: contact.assigned_to ? `Assigned to: ${contact.assigned_to}` : null, key: 'assigned_to' },
      { icon: null, label: contact.last_contacted ? `Last contacted: ${formatDate(contact.last_contacted)}` : null, key: 'last_contacted' },
      // days since last contact 
      { icon: null, label: contact.last_contacted ? `Days since last contact: ${Math.floor((Date.now() - new Date(contact.last_contacted)?.getTime()) / (1000 * 60 * 60 * 24))} days` : null, key: 'days_since_last_contact' },
      { icon: null, label: contact.admin_notes, key: 'admin_notes', tooltip: true }
    ];

    return details
      .filter(detail => detail.label) // Filter out null, undefined, or empty labels
      .map(detail => (
        <Typography
          variant="body2"
          color="textSecondary"
          key={detail.key}
          sx={{ maxHeight: detail.tooltip ? 50 : 'auto', overflow: 'scroll', position: 'relative' }}
          title={detail.tooltip ? String(detail.label) : ''} // Convert to string
        >
          {detail.icon && React.cloneElement(detail.icon, { fontSize: 'inherit', sx: { verticalAlign: 'middle', mr: 0.5 } })}
          {detail.label}
        </Typography>
      ));
  };

  const handleContactUpdate = (updatedContact) => {
    // Update local state or perform additional actions if needed
  };

  const handleDialogOpen = () => {
    openDrawer(contactId);
  };

  if (!contact) {
    // Still loading - the contact will be fetched via the queue
    return <Typography>Loading...</Typography>;
  }

  return (
    <>
      <MuiCard sx={{ mb: 2 }}>
        <CardHeader
          avatar={
            <>
              <Avatar
                src={contact.picture_url || '/api/placeholder/48/48'}
                alt={getFirstLine()}
                sx={{ width: 24, height: 24, '& img': { objectFit: 'cover' } }}
                onClick={(event) => {
                  event.preventDefault();
                  handleDialogOpen(); // Open drawer on avatar click
                }}
              />
            </>
          }
          action={
            <IconButton onClick={handleFavoriteToggle}>
              {isFavorite ? <Star color="warning" /> : <StarBorder />}
            </IconButton>
          }
          title={
            <>
              {getFirstLine()}
              {contact.email && contact.email !== getFirstLine() && (
                <>
                  <br />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {contact.email}
                    <ContactEmailButton contactId={contactId} size="small" />
                  </Box>
                </>
              )}
              {contact.phone_number && contact.phone_number !== getFirstLine() && (
                <>
                  <br />
                  {contact.phone_number}
                </>
              )}
              {contact.score && (
                <Typography variant="body2" color="textSecondary">
                  Score: {contact.score}
                </Typography>
              )}
            </>
          }
          subheader={contact.company_name || ''}
          onClick={() => {
            toggleExpand();
          }}
          sx={{ cursor: 'pointer' }}
        />
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <CardContent>
            {/* three sidebar dots for settings in top right */}
            {renderContactDetails()}

            {contact.shared_notes && (
              <Typography
                variant="body2"
                mt={2}
                color="textSecondary"
                onClick={(event) => {
                  event.preventDefault();
                  handleDialogOpen();
                }}
                sx={{ cursor: 'pointer' }}
              >
                <CustomMarkdown>{contact.shared_notes}</CustomMarkdown>
              </Typography>
            )}
          </CardContent>
        </Collapse>
      </MuiCard>
    </>
  );
}, (prevProps: Readonly<ContactCardV2Props>, nextProps: Readonly<ContactCardV2Props>) => {
  return prevProps.contactId === nextProps.contactId && prevProps.expanded === nextProps.expanded;
});

// Set display name for debugging
ContactCardV2.displayName = 'ContactCardV2';

export default ContactCardV2;
